import { useState, useMemo, useCallback } from 'react';
import './InsightsTable.scss';
import { Download, Link, Tag, Trash2, X } from 'react-feather';
import {
    BaseModal,
    Button,
    DeleteConfirmationDialog,
    FolderSelector,
    IconButton,
    TagsPicker,
} from '../UIElements';
import { BaseTable } from '../Tables';
import { commentsServices } from '~/services';
import { toast } from 'react-hot-toast';
import { useEntityContext, useFloatMenu } from '../../hooks';
import CaretBoxIcon from '../../assets/icons/shared/CaretBoxIcon';
import { Popover } from 'react-tiny-popover';
import { ProjectContext } from '../../context/project';
import { documentServices, tagsServices } from '../../services';
import { TagsContext } from '../../context/tags';
import RestrictedUI from '../RestrictedUI';
import { PERMISSIONS } from '../../constants/memberPermissions';
import { ENTITIES } from '../../constants/entities';
import { ProjectProviderInterface } from '~/interfaces/contexts';
import { TagsProviderInterface } from '~/modules/Tags/types/TagsContext.interface';
import { TopicsContext } from '~/context/topics';
import { useOpenFolders } from '~/hooks';
import { TopicsProviderInterface } from '~/modules/Topics/types/TopicsContext.interface';

const InsightsTable = ({
    intl,
    onScrollTouchesBottom,
    data,
    columns,
    paging = {},
    loading,
    setLoading,
    onDownload,
    onMarkWithTopic,
    showMarkWithTopic,
    lite,
    onAddNewColumn,
    handleColumnsOrder,
    reorderableColumns,
    onUpdateCallback,
    onCopyLinkToSelection,
    tableHeight,
}) => {
    const deleteMenu = useFloatMenu();
    const topicMenu = useFloatMenu();
    const tagsMenu = useFloatMenu();

    const { tags, getTags, transformedFolderStructure } =
        useEntityContext<TagsProviderInterface>(TagsContext);

    const { selectedProject } =
        useEntityContext<ProjectProviderInterface>(ProjectContext);

    const { filledFolderTree } =
        useEntityContext<TopicsProviderInterface>(TopicsContext);

    const [loadingTags, setLoadingTags] = useState(false);
    const [documentTags, setDocumentTags] = useState([]);

    const onRemoveInsight = async () => {
        setLoading(true);

        const insightsToBeRemoved = paging.selectedAll
            ? data.map((item) => item.id)
            : paging.selectedItems;

        let error: boolean | null = null;
        for (const item of insightsToBeRemoved) {
            const [deleteError] = await commentsServices.deleteComment(item);
            if (deleteError) {
                error = deleteError;
                break;
            }
        }

        setLoading(false);

        if (error) {
            toast.error(intl.formatMessage({ id: 'general_error' }));
            return;
        }

        toast.success(
            intl.formatMessage({ id: 'insight_removed_successfully' })
        );
        deleteMenu.handleMenu();
        onUpdateCallback();
    };

    const addTagsToFile = async (newTags) => {
        setLoadingTags(true);

        const tagsToAdd: string[] = newTags
            .filter((t) => !t.isNew)
            .map((t) => t.value);

        const tagsToCreate = newTags.filter((t) => t.isNew).map((t) => t.value);

        const tagsToRemove: string[] = commonDocumentTags.ids?.filter(
            (t) => !tagsToAdd.includes(t)
        );

        if (tagsToCreate.length) {
            const projectId =
                selectedProject?.id ||
                data.find((insight) => insight.id === paging.selectedItems[0])
                    ?.project.id;

            const tagPromises = tagsToCreate.map((t) =>
                tagsServices.createTag({
                    project_id: projectId,
                    name: t,
                })
            );

            const response = await Promise.all(tagPromises);

            response.forEach(
                ([error, response]) =>
                    !error && tagsToAdd.push(response.data.id)
            );
        }

        // get all documents ids
        const docIds: string[] = data
            .filter((s) => paging.selectedItems.includes(s.id))
            .map((s) => s.document_id);

        const payload = {
            document_ids: docIds,
            add_tag_ids: tagsToAdd,
            remove_tag_ids: tagsToRemove || [],
        };

        const [error] = await documentServices.updateDocumentsTags(payload);

        setLoadingTags(false);

        if (error) {
            toast.error(intl.formatMessage({ id: 'general_error' }));
            return;
        }

        toast.success(intl.formatMessage({ id: 'tags_updated_successfully' }));

        onUpdateCallback();

        getTags();
        tagsMenu.handleMenu();
    };

    const getFileTags = useCallback(async () => {
        let docTags = [];

        if (selectedProject) {
            docTags = tags;
        } else {
            setLoadingTags(true);

            const insight = data.find((s) => s.id === paging.selectedItems[0]);

            const [error, response] = await tagsServices.getTags({
                project_ids: insight.project.id,
            });

            if (error) {
                docTags = [];
            } else {
                docTags = response.data;
            }

            setLoadingTags(false);
        }

        const tagsArray = docTags.map((t) => ({ label: t.name, value: t.id }));

        setDocumentTags(tagsArray);
    }, [paging.selectedItems, tags, setDocumentTags]);

    const commonDocumentTags = useMemo(() => {
        if (!documentTags.length)
            return {
                options: [],
                ids: [],
            };

        // get all the insights that are selected
        const items = data.filter((insight) =>
            paging.selectedItems.includes(insight.id)
        );

        // get the tags of each document
        const insightTags = items.map(
            (insight) => new Set(insight.document_tags.map((t) => t.id))
        );

        // get the common tags
        let common = Array.from(insightTags[0]);
        for (const tag of insightTags) {
            common = common.filter((t) => tag.has(t));
        }

        return {
            options: documentTags.filter((t) => common.includes(t.value)),
            ids: common,
        };
    }, [documentTags]);

    const onScrollToBottom = () => {
        const { pageNumber, totalPages } = paging;

        if (pageNumber + 1 === totalPages) return;

        onScrollTouchesBottom();
    };

    const { openedFolders, openFolder, resetOpenedFolders } = useOpenFolders();

    return (
        <div className="cg-insights-table animate__animated animate__fadeIn">
            {deleteMenu.showMenu && (
                <DeleteConfirmationDialog
                    intl={intl}
                    handleClose={deleteMenu.handleMenu}
                    callback={onRemoveInsight}
                    buttonText={intl.formatMessage({ id: 'delete' })}
                    text={intl.formatMessage({
                        id: 'this_action_cannot_be_undone',
                    })}
                    title={intl.formatMessage({ id: 'delete_insight' }) + '?'}
                    loading={loading}
                />
            )}

            {topicMenu.showMenu && (
                <BaseModal
                    showCloseIcon
                    handleClose={topicMenu.handleMenu}
                    noPadding
                >
                    <FolderSelector
                        structure={filledFolderTree}
                        onConfirm={(topicId) => {
                            onMarkWithTopic(topicId);
                            resetOpenedFolders();
                            topicMenu.handleMenu();
                        }}
                        onCancel={topicMenu.handleMenu}
                        expandedFolders={openedFolders}
                        onExpandFolder={openFolder}
                        placeholder={intl.formatMessage({
                            id: 'search_folders_and_topics',
                        })}
                        confirmButtonText={intl.formatMessage({ id: 'save' })}
                        showFiles
                        showUncategorizedFolder
                    />
                </BaseModal>
            )}

            {paging.selectedItems.length > 0 || paging.selectedAll ? (
                <div className="cg-insights-table__header animate__animated animate__fadeIn">
                    {!lite && (
                        <div>
                            <RestrictedUI
                                to={PERMISSIONS.EDIT}
                                entity={ENTITIES.INSIGHTS}
                            >
                                {showMarkWithTopic && (
                                    <Button
                                        onClick={topicMenu.handleMenu}
                                        variant="secondary"
                                        iconBefore={CaretBoxIcon}
                                        disabled={loading}
                                    >
                                        <span>
                                            {intl.formatMessage({
                                                id: 'mark_with_topic',
                                            })}
                                        </span>
                                    </Button>
                                )}
                            </RestrictedUI>

                            <Button
                                variant="secondary"
                                iconBefore={Link}
                                onClick={onCopyLinkToSelection}
                            >
                                <span>
                                    {intl.formatMessage({
                                        id: 'copy_link_to_selection',
                                    })}
                                </span>
                            </Button>

                            <RestrictedUI
                                to={PERMISSIONS.EDIT}
                                entity={ENTITIES.INSIGHTS}
                            >
                                <Popover
                                    isOpen={tagsMenu.showMenu}
                                    positions={['bottom', 'left', 'right']}
                                    align="start"
                                    onClickOutside={() => {
                                        tagsMenu.setShowMenu(false);
                                        setDocumentTags([]);
                                    }}
                                    content={
                                        <TagsPicker
                                            onApply={addTagsToFile}
                                            intl={intl}
                                            tags={documentTags}
                                            selectedTags={
                                                commonDocumentTags.options
                                            }
                                            loading={loadingTags}
                                            folderStructure={
                                                transformedFolderStructure
                                            }
                                        />
                                    }
                                >
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            tagsMenu.handleMenu();
                                            getFileTags();
                                        }}
                                        iconBefore={Tag}
                                    >
                                        <span>
                                            {intl.formatMessage({
                                                id:
                                                    paging.selectedItems
                                                        .length > 1
                                                        ? 'tag_files'
                                                        : 'tag_file',
                                            })}
                                        </span>
                                    </Button>
                                </Popover>
                            </RestrictedUI>

                            <Button
                                variant="secondary"
                                onClick={() => onDownload(false)}
                                iconBefore={Download}
                                disabled={loading}
                            >
                                {intl.formatMessage({ id: 'download' })} .xlsx
                            </Button>

                            <RestrictedUI
                                to={PERMISSIONS.EDIT}
                                entity={ENTITIES.INSIGHTS}
                            >
                                <IconButton
                                    onClick={deleteMenu.handleMenu}
                                    variant="secondary"
                                    icon={Trash2}
                                    disabled={loading}
                                />
                            </RestrictedUI>
                        </div>
                    )}

                    <Button
                        variant="tertiary"
                        onClick={() => {
                            paging.onSelectItem('');
                            paging.setSelectedAll(false);
                        }}
                    >
                        <X /> {intl.formatMessage({ id: 'clear_selection' })}
                    </Button>
                </div>
            ) : null}

            <BaseTable
                data={data}
                columns={columns}
                loading={loading}
                onSelectionChange={paging.setSelectedItems}
                selectedItems={paging.selectedItems}
                onScrollToBottom={onScrollToBottom}
                onAddNewColumn={onAddNewColumn}
                intl={intl}
                tableHeight={tableHeight}
                handleColumnsOrder={handleColumnsOrder}
                reorderableColumns={reorderableColumns}
                minWidth={900}
            />
        </div>
    );
};

export default InsightsTable;
